import { USD } from "@dinero.js/currencies";
import { dinero } from "dinero.js";
import _ from "lodash";
import { z } from "zod";
import { BaseConnector } from "../baseConnector";
export class MouserConnector extends BaseConnector {
    getApiKey;
    constructor({ getApiKey }) {
        super();
        this.getApiKey = getApiKey;
    }
    get baseUrl() {
        return "https://api.mouser.com/api";
    }
    async search(params) {
        let response;
        if ("mpn" in params) {
            if (params.manufacturer) {
                response = await this.searchByPartNumberAndManufacturer(params.mpn, params.manufacturer);
            }
            else {
                response = await this.searchByPartNumber(params.mpn);
            }
        }
        else if ("keyword" in params) {
            if (params.manufacturer) {
                response = await this.searchByKeywordAndManufacturer(params.keyword, params.manufacturer);
            }
            else {
                response = await this.searchByKeyword(params.keyword);
            }
        }
        else {
            throw new Error("Invalid search params");
        }
        const searchResults = processSearchResponse(response);
        return searchResults;
    }
    async searchByKeyword(keyword) {
        const res = await this.post("/v1/search/keyword", {
            SearchByKeywordRequest: {
                keyword,
            },
        });
        return MouserSearchResponseSchema.parse(res.payload);
    }
    async searchByKeywordAndManufacturer(keyword, manufacturer) {
        const res = await this.post("/v2/search/keywordandmanufacturer", {
            SearchByKeywordMfrNameRequest: {
                manufacturer,
                keyword,
            },
        });
        return MouserSearchResponseSchema.parse(res.payload);
    }
    async searchByPartNumber(partNumber) {
        const res = await this.post("/v1/search/partnumber", {
            SearchByPartRequest: {
                mouserPartNumber: partNumber,
            },
        });
        return MouserSearchResponseSchema.parse(res.payload);
    }
    async searchByPartNumberAndManufacturer(partNumber, manufacturer) {
        const res = await this.post("/v2/search/partnumberandmanufacturer", {
            SearchByPartMfrNameRequest: {
                mouserPartNumber: partNumber,
                manufacturerName: manufacturer,
            },
        });
        return MouserSearchResponseSchema.parse(res.payload);
    }
    async getRequestConfig() {
        return {
            params: { apiKey: this.getApiKey() },
        };
    }
}
function processSearchResponse(response) {
    if (!_.isEmpty(response.Errors) || !response.SearchResults) {
        throw new Error(response.Errors.map((e) => e.Message).join("; "));
    }
    return response.SearchResults.Parts.map((part) => ({
        distributor: "mouser",
        mpn: part.ManufacturerPartNumber,
        dpn: part.MouserPartNumber, // NOTE: Mouser will sometimes return "N/A" for this field if the part is not available.
        // This can be problematic if we're expecting the DPN to be unique.
        availableQuantity: parseInt(part.AvailabilityInStock ?? "-1"),
        minimumOrderQuantity: parseInt(part.Min),
        datasheetUrl: part.DataSheetUrl,
        description: part.Description,
        leadTimeDays: parseInt(part.LeadTime),
        lifecycleStatus: part.LifecycleStatus,
        manufacturerName: part.Manufacturer ?? "",
        packagingOptions: part.ProductAttributes.reduce((opts, a) => {
            if (a.AttributeName === "Packaging") {
                opts.push(a.AttributeValue);
            }
            return opts;
        }, []),
        priceBreaks: part.PriceBreaks.map((pb) => ({
            quantity: pb.Quantity,
            price: parseUSD(pb.Price),
            currency: pb.Currency,
        })),
        listingUrl: part.ProductDetailUrl,
        distributorAttributes: {
            distributor: "mouser",
            Category: part.Category,
            ImagePath: part.ImagePath,
            FactoryStock: part.FactoryStock,
            Mult: part.Mult,
            AlternatePackagings: part.AlternatePackagings,
            AvailabilityOnOrder: part.AvailabilityOnOrder,
            InfoMessages: part.InfoMessages,
            ROHSStatus: part.ROHSStatus,
            SuggestedReplacement: part.SuggestedReplacement,
            MultiSimBlue: part.MultiSimBlue,
            Reeling: part.Reeling,
            ProductCompliance: part.ProductCompliance.reduce((acc, c) => {
                acc[c.ComplianceName] = c.ComplianceValue;
                return acc;
            }, {}),
        },
    }));
}
function parseUSD(price) {
    const decimal = price.replace("$", "").replace(",", "");
    return dinero({ amount: Math.round(parseFloat(decimal) * 100000), scale: 5, currency: USD });
}
const MouserSearchErrorSchema = z.object({
    Id: z.number(),
    Code: z.string(),
    Message: z.string(),
    ResourceKey: z.string().optional(),
    ResourceFormatString: z.string().optional().nullable(),
    ResourceFormatString2: z.string().optional().nullable(),
    PropertyName: z.string().optional().nullable(),
});
const MouserSearchResultsSchema = z.object({
    NumberOfResult: z.number(),
    Parts: z.lazy(() => z.array(MouserPartSchema)),
});
const MouserSearchResponseSchema = z.object({
    Errors: z.array(MouserSearchErrorSchema),
    SearchResults: MouserSearchResultsSchema.nullable(),
});
const MouserPartSchema = z.object({
    Availability: z.string().optional(),
    DataSheetUrl: z.string(),
    Description: z.string(),
    FactoryStock: z.string().nullable(),
    ImagePath: z.string().nullable(),
    Category: z.string(),
    LeadTime: z.string(),
    LifecycleStatus: z.string().nullable(),
    Manufacturer: z.string().nullable(),
    ManufacturerPartNumber: z.string(),
    Min: z.string(),
    Mult: z.string(),
    MouserPartNumber: z.string(),
    ProductAttributes: z.array(z.object({
        AttributeName: z.string(),
        AttributeValue: z.string(),
    })),
    PriceBreaks: z.array(z.object({
        Quantity: z.number(),
        Price: z.string(),
        Currency: z.string(),
    })),
    AlternatePackagings: z.array(z.record(z.string())).nullable(),
    ProductDetailUrl: z.string(),
    Reeling: z.boolean(),
    ROHSStatus: z.string(),
    SuggestedReplacement: z.string(),
    MultiSimBlue: z.number(),
    AvailabilityInStock: z.string().nullable(),
    AvailabilityOnOrder: z.array(z.object({
        Quantity: z.number(),
        Date: z.string(),
    })),
    InfoMessages: z.array(z.string()),
    ProductCompliance: z.array(z.object({
        ComplianceName: z.string(),
        ComplianceValue: z.string(),
    })),
});
//# sourceMappingURL=mouser.js.map